import { createAsyncThunk } from '@reduxjs/toolkit'

import { callErrorMsg } from 'helpers/errorMsg'
import { getHubs } from 'utils/api'
import { downloadFileBlob, downloadFileUrl, base64ToBlob } from 'utils/downloadFileUrl'
import { dateFormat, stringToDateObject } from 'utils/helpers/date'
import { toastSuccess } from 'utils/toast'
import {
  getMasterPayrollListV2,
  getDetailPayrollByUserIdServices,
  getMasterPayrollPayslipPdf,
  postPublishPayrollByFilter,
  postPublishPayrollByIds,
  getPayrollAttributeExportTemplate,
} from 'features/Enterprise/Payroll/services'
import { toPayrollTypeApi } from 'features/Enterprise/Payroll/utils'
import { PayslipOwnerEnum } from 'features/Enterprise/Payroll/types'

import {
  setHubList,
  setLoading,
  sliceName,
  setMasterPayrollList,
  setPayrollDetailByUserId,
  setQuery,
  setQueryPayrollDetailByUserId,
} from './masterPayrollSlice'

export const getHubList = createAsyncThunk(`${sliceName}/getHubList`, async (_, { dispatch }) => {
  try {
    dispatch(setLoading(true))
    const { data } = await getHubs()
    if (data?.length) {
      dispatch(
        setHubList([
          // TODO(WP-4366): Preserve string '0'; Payroll's UI ID contract needs a sentinel union.
          { location_id: '0' as unknown as number, location_name: 'All Hub', location_type: 'HUB' },
          ...data,
        ]),
      )
    }
  } catch (error) {
    callErrorMsg(error)
  } finally {
    dispatch(setLoading(false))
  }
})

export const getMasterList = createAsyncThunk(
  `${sliceName}/getMasterList`,
  async (
    payload: {
      date: string
      location_id: string
      search: string
      cursor: string
      location_type: string
      payroll_type?: string
      searchBy: 'staff_id' | 'nama'
      page?: number | string
      publish_status?: string
    },
    { dispatch, getState },
  ) => {
    try {
      const {
        masterPayroll: { query },
      } = getState() as StoreStateType
      dispatch(setLoading(true))
      const {
        date,
        location_id: locationId,
        search,
        cursor,
        payroll_type: payrollType,
        searchBy,
        page,
        publish_status: publishStatus,
      } = payload
      const [splitedDate] = date.split('-')
      const dateObj = stringToDateObject(splitedDate, 'YYYYMMDD')
      const month = dateFormat({ date: dateObj, format: 'M' })
      const year = dateFormat({ date: dateObj, format: 'YYYY' })
      const params: Parameters<typeof getMasterPayrollListV2>[0] = {
        cursor: cursor || '',
        location_id: locationId,
        month,
        page_size: query.pageSize,
        year,
      }
      if (payrollType && payrollType !== 'all') {
        params.payroll_type = toPayrollTypeApi(payrollType)
      }
      if (search) {
        if (searchBy === 'staff_id') {
          params.staff_id = search
        } else {
          params.staff_name = search
        }
      }
      if (publishStatus) {
        params.publish_status = publishStatus
      }
      const {
        data: { data, pagination },
      } = await getMasterPayrollListV2(params)
      const pageIndex = page ? Number(page) : 1
      dispatch(
        setQuery({
          ...query,
          pageIndex,
          cursor: pagination.prev_cursor,
          nextCursor: pagination.next_cursor,
          numberOfElements: data.length,
        }),
      )
      dispatch(setMasterPayrollList(data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const getPayrollByUserId = createAsyncThunk(
  `${sliceName}/getPayrollByUserId`,
  async (
    payload: Pick<Parameters<typeof getDetailPayrollByUserIdServices>[0], 'staffId'>,
    { dispatch, getState },
  ) => {
    const {
      masterPayroll: { queryPayrollDetailByUserId },
    } = getState() as StoreStateType
    try {
      dispatch(setLoading(true))
      const {
        data: {
          data,
          pagination: { number_of_elements, page_index },
        },
      } = await getDetailPayrollByUserIdServices({ ...payload, ...queryPayrollDetailByUserId })
      dispatch(
        setQueryPayrollDetailByUserId({
          ...queryPayrollDetailByUserId,
          numberOfElements: number_of_elements,
          pageIndex: page_index,
        }),
      )
      dispatch(setPayrollDetailByUserId(data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const exportPayroll = createAsyncThunk(
  `${sliceName}/exportPayroll`,
  async (
    payload: {
      date: string
      location_id: string
      search: string
      payroll_type?: string
    },
    { dispatch },
  ) => {
    const { location_id: locationId, date, payroll_type: payrollType } = payload
    const [splitedDate] = date.split('-')
    const dateObj = stringToDateObject(splitedDate, 'YYYYMMDD')
    const monthNum = Number(dateFormat({ date: dateObj, format: 'M' }))
    const year = dateFormat({ date: dateObj, format: 'YYYY' })
    const startDate = dateFormat({
      date: new Date(Number(year), monthNum - 1, 1),
      format: 'YYYYMMDD',
    })
    const endDate = dateFormat({ date: new Date(Number(year), monthNum, 0), format: 'YYYYMMDD' })
    try {
      dispatch(setLoading(true))
      const { data } = await getPayrollAttributeExportTemplate({
        location_id: locationId,
        start_date: startDate,
        end_date: endDate,
        ...(payrollType && payrollType !== 'all'
          ? { payroll_type: toPayrollTypeApi(payrollType) }
          : {}),
        owner: PayslipOwnerEnum.PEOPLE,
      })
      downloadFileUrl(data.data.file_url)
    } catch (error) {
      callErrorMsg(error, 'Gagal export payroll')
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const getPaySlipPdf = createAsyncThunk(
  `${sliceName}/getPaySlipPdf`,
  async (
    payload: Parameters<typeof getMasterPayrollPayslipPdf>[0] & {
      personName: string
      date: string
      isPreview?: boolean
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const { personName, date, isPreview, ...restPayload } = payload
      const { data } = await getMasterPayrollPayslipPdf(restPayload)
      downloadFileBlob({
        data: base64ToBlob(data.data, 'application/pdf'),
        mimeType: 'application/pdf',
        fileName: `payslip_${personName}_${date}`,
        isOpenNewTab: isPreview,
      })
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const publishPayroll = createAsyncThunk(
  `${sliceName}/publishPayroll`,
  async (
    payload: {
      date: string
      location_id: string
      search: string
      payroll_type?: string
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoading(true))
      const { location_id: locationId, date, payroll_type: payrollType } = payload
      const [splitedDate] = date.split('-')
      const dateObj = stringToDateObject(splitedDate, 'YYYYMMDD')
      const month = dateFormat({ date: dateObj, format: 'M' })
      const year = dateFormat({ date: dateObj, format: 'YYYY' })

      await postPublishPayrollByFilter({
        location_id: locationId,
        month,
        year,
        ...(payrollType && payrollType !== 'all'
          ? { payroll_type: toPayrollTypeApi(payrollType) }
          : {}),
      })
      toastSuccess('Publish payroll berhasil')
    } catch (error) {
      callErrorMsg(error, 'Gagal publish payroll')
      throw error
    } finally {
      dispatch(setLoading(false))
    }
  },
)

export const publishPayrollByIds = createAsyncThunk(
  `${sliceName}/publishPayrollByIds`,
  async (payload: { ids: string[] }, { dispatch }) => {
    dispatch(setLoading(true))
    try {
      await postPublishPayrollByIds(payload)
      toastSuccess('Publish payroll berhasil')
    } finally {
      dispatch(setLoading(false))
    }
  },
)
